import { useId } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"

interface LogoProps {
  className?: string
  variant?: "full" | "icon"
  size?: "sm" | "md" | "lg"
}

export function Logo({
  className = "",
  variant = "full",
  size = "md",
}: LogoProps) {
  const { t } = useTranslation()
  // 사이드바·모바일 헤더·드로어에 동시에 렌더되므로 그라디언트 id는 인스턴스마다 다르게
  const gradientId = `logo-gradient-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`
  const sizeClasses = {
    sm: "w-6 h-6",
    md: "w-8 h-8",
    lg: "w-10 h-10",
  }

  const textSizeClasses = {
    sm: "text-lg",
    md: "text-xl",
    lg: "text-2xl",
  }

  // 웍스 3색 레이어 스택: 시안 카드 위에 그라디언트 카드, 앞 카드에는 이미지 생성(산 + 스파클). public/favicon.svg와 같은 도형
  const IconContent = (
    <svg
      className={cn(
        "shrink-0 drop-shadow-[0_2px_6px_rgb(74_85_240/0.25)] transition-transform duration-300 group-hover:scale-105",
        sizeClasses[size]
      )}
      viewBox="0 0 64 64"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="4" y1="4" x2="60" y2="60">
          <stop offset="0" style={{ stopColor: "var(--brand-indigo)" }} />
          <stop offset="1" style={{ stopColor: "var(--brand-violet)" }} />
        </linearGradient>
      </defs>
      <rect x="17" y="4" width="43" height="43" rx="12" style={{ fill: "var(--brand-cyan)" }} />
      <rect x="4" y="17" width="43" height="43" rx="12" fill={`url(#${gradientId})`} />
      <polygon
        points="12,51 21.5,39 27.5,46 31,42.5 39,51"
        fill="#fff"
        stroke="#fff"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path d="M32.5 22Q34.32 26.68 39 28.5Q34.32 30.32 32.5 35Q30.68 30.32 26 28.5Q30.68 26.68 32.5 22Z" fill="#fff" />
    </svg>
  )

  if (variant === "icon") {
    return IconContent
  }

  return (
    <div className={cn("group flex items-center gap-2.5 min-w-0", className)}>
      {IconContent}
      <span
        className={cn(
          "font-bold tracking-tight truncate text-slate-900",
          textSizeClasses[size]
        )}
      >
        {t('pages.appName')}
      </span>
    </div>
  )
}
